import PendingNotificationIcon from "components/PendingNotification/PendingNotification";
import { NOT_SCHEDULED_DATE, orderStatusConstants } from "constants/order";
import { parseDate } from "helpers/date";
import { centsToDollars, parsePrice } from "helpers/price";
import { HorizontalLine } from "pages/Dashboard/components/styled";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { Message, MessageSeverityType } from "primereact/message";
import { Row } from "primereact/row";
import { useMemo, useState } from "react";
import styled from "styled-components";

const StyledWarning = styled(Message)`
  background: transparent !important;
  padding: 0 !important;
`;

const StyledWarningDescription = styled.p`
  color: var(--gray-new-500) !important;
`;

function patientFirstNameColumn(row: any) {
  return <span data-pii="patient-first-name">{row.patientFirstName}</span>;
}
function patientLastNameColumn(row: any) {
  return <span data-pii="patient-last-name">{row.patientLastName}</span>;
}

export function MatchingOrders({
  items,
  severity,
  textTop,
  textBottom,
  buttonLabel,
  onButtonClick,
  buttonDisabled = false,
  buttonTooltip,
}: {
  items: any[];
  severity: MessageSeverityType;
  textTop: string;
  textBottom: string;
  buttonLabel: string;
  onButtonClick: () => void;
  buttonDisabled?: boolean;
  buttonTooltip?: string;
}) {
  const [showMatchingOrders, setShowMatchingOrders] = useState(false);
  function orderIdColumn(row: any) {
    const url =
      row.orderStatus === orderStatusConstants.DRAFT
        ? `/orders/review/${row.id}?accountId=${row.accountId}`
        : `/orders/${row.id}`;
    return (
      <Button
        className="p-button-link p-0"
        data-testid="order_action"
        onClick={() => window.open(url, "_blank")}
      >
        <p className="small-text">View estimate</p>
      </Button>
    );
  }

  return (
    <div className="flex flex-column gap-2 px-3 mt-4">
      <div className="w-full flex justify-content-between align-items-center">
        <StyledWarning
          severity={severity}
          text={textTop}
          content={
            <div className="w-full flex gap-3 align-items-center">
              {severity === "success" ? (
                <i className="pi pi-check" />
              ) : (
                <i
                  className="pi pi-bell"
                  style={{ fontSize: "1.5rem", color: "var(--bluegray-500)" }}
                />
              )}
              <div className="flex flex-column gap-1">
                <p className="text-md font-semibold">{textTop}</p>
                <StyledWarningDescription className="text-sm font-normal">
                  {textBottom}
                </StyledWarningDescription>
              </div>
            </div>
          }
        />
        <div className="flex gap-2">
          <Button
            label={buttonLabel}
            className="p-button-primary w-fit py-2"
            onClick={onButtonClick}
            disabled={buttonDisabled}
            tooltip={buttonDisabled ? buttonTooltip : undefined}
            tooltipOptions={{ showOnDisabled: true }}
          />
          <Button
            label={showMatchingOrders ? "Hide Details" : "Show Details"}
            className="p-button-sm p-button-outlined py-2"
            onClick={() => setShowMatchingOrders(!showMatchingOrders)}
            icon={
              showMatchingOrders ? "pi pi-chevron-up" : "pi pi-chevron-down"
            }
          />
        </div>
      </div>
      {showMatchingOrders && <HorizontalLine className="my-0" />}
      {showMatchingOrders && (
        <DataTable
          emptyMessage="No matching estimates found."
          stripedRows
          className="p-fluid w-full"
          responsiveLayout="scroll"
          value={items}
        >
          <Column
            field="patientFirstName"
            header="Patient First Name"
            headerStyle={{ width: "15%" }}
            body={patientFirstNameColumn}
          />
          <Column
            field="patientLastName"
            header="Patient Last Name"
            headerStyle={{ width: "15%" }}
            body={patientLastNameColumn}
          />
          <Column
            field="dateOfService"
            header="Date Of Service"
            headerStyle={{ width: "15%" }}
            body={(rowData) =>
              parseDate(rowData.dateOfService) === NOT_SCHEDULED_DATE
                ? "Not scheduled"
                : parseDate(rowData.dateOfService)
            }
          />
          <Column
            field="deductibleReductionInCents"
            header="Deductible Reduction"
            headerStyle={{ width: "15%" }}
            body={(rowData) =>
              parsePrice(
                centsToDollars(rowData.deductibleReductionInCents || 0)
              )
            }
          />
          <Column
            field="oopReductionInCents"
            header="Out-of-pocket Reduction"
            headerStyle={{ width: "15%" }}
            body={(rowData) =>
              parsePrice(centsToDollars(rowData.oopReductionInCents || 0))
            }
          />
          <Column
            field="id"
            header="Estimate"
            headerStyle={{ width: "10%" }}
            body={(row) => orderIdColumn(row)}
          />
        </DataTable>
      )}
    </div>
  );
}
